import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import LinkedInService from '../../services/agentProfile/LinkedInService';

const LinkedInSyncButton = ({ linkedInStatus, onSyncComplete }) => {
    const [syncing, setSyncing] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const toast = useRef(null);

    // Disposer returned by openOAuthPopup (#218). Unmounting with the LinkedIn
    // popup still open is an exit path the service cannot observe, so without
    // this the poll timer and the global `message` handler outlive the
    // component and its setState closures.
    const oauthCleanup = useRef(null);

    useEffect(() => () => {
        oauthCleanup.current?.();
        oauthCleanup.current = null;
    }, []);

    const handleSyncClick = () => {
        if (linkedInStatus?.is_connected) {
            // Already connected - trigger re-sync
            setShowConfirmDialog(true);
        } else {
            // Not connected - start OAuth flow
            initiateLinkedInConnect();
        }
    };

    const initiateLinkedInConnect = async () => {
        try {
            setSyncing(true);

            // Get OAuth URL from backend
            const response = await LinkedInService.getConnectUrl();

            if (response.success && response.data.auth_url) {
                // Open OAuth popup
                oauthCleanup.current = LinkedInService.openOAuthPopup(
                    response.data.auth_url,
                    handleOAuthSuccess,
                    handleOAuthError
                );
            } else {
                throw new Error('Failed to get LinkedIn authorization URL');
            }
        } catch (error) {
            console.error('Error initiating LinkedIn connect:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Connection Failed',
                detail: error.message || 'Failed to connect to LinkedIn',
                life: 5000
            });
            setSyncing(false);
        }
    };

    const handleOAuthSuccess = async (data) => {
        setSyncProgress({
            step: 1,
            message: 'LinkedIn connected! Syncing your profile...'
        });

        try {
            // Trigger sync
            const syncResponse = await LinkedInService.syncProfile();

            setSyncProgress({
                step: 2,
                message: 'Profile synced successfully!'
            });

            setTimeout(() => {
                setSyncing(false);
                setSyncProgress(null);
                setShowConfirmDialog(false);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Your profile has been synced with LinkedIn!',
                    life: 5000
                });

                // Notify parent component
                if (onSyncComplete) {
                    onSyncComplete(syncResponse.data);
                }
            }, 1500);
        } catch (error) {
            console.error('Error syncing profile:', error);
            setSyncing(false);
            setSyncProgress(null);

            toast.current?.show({
                severity: 'error',
                summary: 'Sync Failed',
                detail: 'Failed to sync profile data from LinkedIn',
                life: 5000
            });
        }
    };

    const handleOAuthError = (error) => {
        console.error('OAuth error:', error);
        setSyncing(false);
        setSyncProgress(null);

        toast.current?.show({
            severity: 'error',
            summary: 'Connection Failed',
            detail: error.message || 'Failed to connect to LinkedIn',
            life: 5000
        });
    };

    const handleResync = async () => {
        try {
            setSyncing(true);
            setShowConfirmDialog(false);

            setSyncProgress({
                step: 1,
                message: 'Fetching latest data from LinkedIn...'
            });

            const response = await LinkedInService.syncProfile();

            setSyncProgress({
                step: 2,
                message: 'Profile updated successfully!'
            });

            setTimeout(() => {
                setSyncing(false);
                setSyncProgress(null);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Your profile has been updated with latest LinkedIn data!',
                    life: 5000
                });

                if (onSyncComplete) {
                    onSyncComplete(response.data);
                }
            }, 1500);
        } catch (error) {
            console.error('Error re-syncing profile:', error);
            setSyncing(false);
            setSyncProgress(null);

            toast.current?.show({
                severity: 'error',
                summary: 'Sync Failed',
                detail: error.response?.data?.message || 'Failed to sync with LinkedIn',
                life: 5000
            });
        }
    };

    const handleDisconnect = async () => {
        try {
            await LinkedInService.disconnect();

            toast.current?.show({
                severity: 'info',
                summary: 'Disconnected',
                detail: 'LinkedIn account has been disconnected',
                life: 3000
            });

            if (onSyncComplete) {
                onSyncComplete(null);
            }
        } catch (error) {
            console.error('Error disconnecting LinkedIn:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to disconnect LinkedIn',
                life: 3000
            });
        }
    };

    const confirmDialogFooter = (
        <div className="flex gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={() => setShowConfirmDialog(false)}
                className="p-button-text"
            />
            <Button
                label="Disconnect"
                icon="pi pi-unlink"
                onClick={handleDisconnect}
                className="p-button-outlined p-button-danger"
            />
            <Button
                label="Re-sync Now"
                icon="pi pi-refresh"
                onClick={handleResync}
                autoFocus
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />

            <Button
                label={linkedInStatus?.is_connected ? 'Sync with LinkedIn' : 'Connect LinkedIn'}
                icon={syncing ? 'pi pi-spin pi-spinner' : 'pi pi-linkedin'}
                onClick={handleSyncClick}
                disabled={syncing}
                className={linkedInStatus?.is_connected ? 'p-button-outlined' : ''}
            />

            {/* Sync Progress Dialog */}
            <Dialog
                visible={syncing && syncProgress}
                onHide={() => {}}
                closable={false}
                showHeader={false}
                modal
                style={{ width: '400px' }}
            >
                <div className="flex flex-column align-items-center gap-4 p-4">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    <div className="text-center">
                        <h6 className="m-0 mb-2">Syncing with LinkedIn</h6>
                        <p className="text-600 m-0">{syncProgress?.message}</p>
                    </div>
                </div>
            </Dialog>

            {/* Re-sync Confirmation Dialog */}
            <Dialog
                visible={showConfirmDialog}
                onHide={() => setShowConfirmDialog(false)}
                header="LinkedIn Profile Sync"
                footer={confirmDialogFooter}
                style={{ width: '500px' }}
            >
                <div className="flex flex-column gap-3">
                    <p className="m-0">
                        <i className="pi pi-check-circle text-green-500 mr-2"></i>
                        Your LinkedIn account is connected
                    </p>

                    {linkedInStatus?.last_synced_at && (
                        <p className="text-600 text-sm m-0">
                            Last synced: {new Date(linkedInStatus.last_synced_at).toLocaleString()}
                        </p>
                    )}

                    <div className="surface-100 border-round p-3 mt-2">
                        <p className="font-medium m-0 mb-2">What would you like to do?</p>
                        <ul className="pl-3 mb-0">
                            <li className="mb-2"><strong>Re-sync Now:</strong> Fetch latest data from LinkedIn and update your profile</li>
                            <li className="mb-2"><strong>Disconnect:</strong> Remove LinkedIn connection (your data will remain)</li>
                        </ul>
                    </div>

                    <p className="text-sm text-500 m-0 mt-2">
                        <i className="pi pi-info-circle mr-2"></i>
                        Re-syncing will update your profile with the latest information from LinkedIn. Any manual edits you made will be preserved unless the same field was updated on LinkedIn.
                    </p>
                </div>
            </Dialog>
        </>
    );
};

export default LinkedInSyncButton;
